import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronDown, Search, X } from "lucide-react";
import {
  initialsOf,
  avatarTone,
} from "./AttendanceBits.jsx";
// Same stylesheet the attendance list uses -- one sheet for the trainer area.
import "../TrainerDashboard.css";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from '../../common/Toast/toast';
import {
  createattendance,
  updateattendance,
  getattendanceById,
  listAttendances,
} from "../../../../services/MarkattendancesService";
import { getBatches, getBatchById } from "../../../../services/BatchService";
import { listSyllabusTopics } from "../../../../services/SyllabusTopicServices.js";
import { resolveCourseId } from "../../../../utils/courseId";
import {
  useNavigate as UseNavigate,
  useParams as UseParams,
} from "react-router-dom";
import { useAuth } from "../../../../context/AuthContext";
import { getMe } from "../../../../services/AuthService";

// ---------------------------------------------------------------------------
// MultiSelectTopicsDropdown
// ---------------------------------------------------------------------------
const MultiSelectTopicsDropdown = ({
  options = [],
  selectedValues = [],
  onChange,
  placeholder = "Select topics...",
  disabled = false,
  taughtTopics = [],
  isCommonOff = false,
  // Rendered as the step's content rather than behind a trigger: on the
  // topics step the list IS the question, so there is nothing to open.
  alwaysOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedAccordions, setExpandedAccordions] = useState(new Set());
  const dropdownRef = useRef(null);

  const groupedTopics = () => {
    const parentTopicsMap = {};
    const childTopicsMap = {};

    options.forEach((topic) => {
      if (topic.parentTopicId === "NA") {
        if (!parentTopicsMap[topic.id]) {
          parentTopicsMap[topic.id] = { ...topic, children: [] };
        }
      } else {
        if (!childTopicsMap[topic.parentTopicId]) {
          childTopicsMap[topic.parentTopicId] = [];
        }
        childTopicsMap[topic.parentTopicId].push(topic);
      }
    });

    Object.keys(parentTopicsMap).forEach((parentId) => {
      if (childTopicsMap[parentId]) {
        parentTopicsMap[parentId].children = childTopicsMap[parentId].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      }
    });

    const sortedParentTopics = Object.values(parentTopicsMap).sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    const standaloneTopics = options
      .filter(
        (topic) =>
          topic.parentTopicId !== "NA" &&
          !Object.keys(parentTopicsMap).includes(topic.parentTopicId)
      )
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    return { parentTopics: sortedParentTopics, standaloneTopics };
  };

  const { parentTopics, standaloneTopics } = groupedTopics();

  // Every group starts open. Collapsed-by-default hid the actual topics
  // behind a second click each, on the one step whose whole job is
  // choosing them.
  useEffect(() => {
    setExpandedAccordions(new Set(parentTopics.map((t) => t.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  const getFilteredAndExpandedTopics = () => {
    if (!searchTerm) {
      return {
        filteredParentTopics: parentTopics,
        filteredStandaloneTopics: standaloneTopics,
        autoExpandedAccordions: new Set(),
      };
    }

    const filteredParentTopics = [];
    const autoExpandedAccordions = new Set();
    const searchLower = searchTerm.toLowerCase();

    parentTopics.forEach((parentTopic) => {
      const parentMatches = parentTopic.topic.toLowerCase().includes(searchLower);
      const matchingChildren = parentTopic.children.filter((child) =>
        child.topic.toLowerCase().includes(searchLower)
      );

      if (parentMatches || matchingChildren.length > 0) {
        filteredParentTopics.push({
          ...parentTopic,
          children: parentMatches ? parentTopic.children : matchingChildren,
        });
        autoExpandedAccordions.add(parentTopic.id);
      }
    });

    const filteredStandaloneTopics = standaloneTopics.filter((topic) =>
      topic.topic.toLowerCase().includes(searchLower)
    );

    return { filteredParentTopics, filteredStandaloneTopics, autoExpandedAccordions };
  };

  const { filteredParentTopics, filteredStandaloneTopics, autoExpandedAccordions } =
    getFilteredAndExpandedTopics();

  useEffect(() => {
    if (searchTerm && autoExpandedAccordions.size > 0) {
      setExpandedAccordions(new Set([...expandedAccordions, ...autoExpandedAccordions]));
    } else if (!searchTerm) {
      setExpandedAccordions(new Set());
    }
  }, [searchTerm, autoExpandedAccordions.size]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggleOption = (topic) => {
    const topicName = topic.topic;
    if (taughtTopics.includes(topicName) || isCommonOff) return;
    const isSelected = selectedValues.includes(topicName);
    const newSelectedValues = isSelected
      ? selectedValues.filter((val) => val !== topicName)
      : [...selectedValues, topicName];
    onChange(newSelectedValues);
  };

  const handleRemoveTag = (optionToRemove) => {
    if (taughtTopics.includes(optionToRemove) || isCommonOff) return;
    onChange(selectedValues.filter((val) => val !== optionToRemove));
  };

  const toggleAccordion = (accordionId) => {
    const newExpanded = new Set(expandedAccordions);
    newExpanded.has(accordionId) ? newExpanded.delete(accordionId) : newExpanded.add(accordionId);
    setExpandedAccordions(newExpanded);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchTerm("");
    setExpandedAccordions(new Set());
  };

  // One tickable row. Children, main topics that have none, and topics
  // whose parent is not in this course all draw the same way -- what a
  // topic sits under is not what makes it teachable.
  const renderTopicOption = (topic) => {
    const isSelected = selectedValues.includes(topic.topic);
    const isTaught = taughtTopics.includes(topic.topic);
    return (
      <label
        key={topic.id}
        className={`att-option${isSelected ? " is-on" : ""}${
          isTaught ? " is-locked" : ""
        }`}
      >
        <input
          type="checkbox"
          checked={isSelected || isTaught}
          disabled={isTaught}
          onChange={() => !isTaught && handleToggleOption(topic)}
        />
        <span className="att-option-text">
          {topic.topic}
          {isTaught && (
            <span className="att-option-note">Previously taught</span>
          )}
        </span>
      </label>
    );
  };

  return (
    <div className="att-picker" ref={dropdownRef}>
      {(selectedValues.length > 0 || isCommonOff) && (
        <div className="att-chips">
          <span className="att-chips-label">
            {isCommonOff ? "Today's status" : "Selected today"}
          </span>
          <div className="att-chips-row">
            {isCommonOff ? (
              <span className="pg-pill pg-pill--warn">
                Common off — all students on leave
              </span>
            ) : (
              selectedValues.map((topic, index) => (
                <span className="att-chip" key={`${topic}-${index}`}>
                  {topic}
                  {!taughtTopics.includes(topic) && (
                    <button
                      type="button"
                      className="att-chip-x"
                      onClick={() => handleRemoveTag(topic)}
                      aria-label={`Remove ${topic}`}
                    >
                      <X size={12} />
                    </button>
                  )}
                </span>
              ))
            )}
          </div>
        </div>
      )}

      {!alwaysOpen && (
      <button
        type="button"
        className="att-trigger"
        disabled={disabled || isCommonOff}
        aria-expanded={isOpen}
        onClick={() => !disabled && !isCommonOff && setIsOpen(!isOpen)}
      >
        <span
          className={
            selectedValues.length === 0 && taughtTopics.length === 0
              ? "att-trigger-text att-trigger-text--empty"
              : "att-trigger-text"
          }
        >
          {isCommonOff
            ? "Topics disabled — common off"
            : selectedValues.length === 0 && taughtTopics.length === 0
              ? placeholder
              : `${taughtTopics.length} previously taught + ${selectedValues.length} today = ${
                  selectedValues.length + taughtTopics.length
                } covered`}
        </span>
        <ChevronDown size={15} className="att-trigger-caret" />
      </button>
      )}

      {(alwaysOpen || isOpen) && !disabled && !isCommonOff && (
        <div className={alwaysOpen ? "att-panel att-panel--inline" : "att-panel"}>
          <div className="att-panel-search">
            <Search size={15} className="att-panel-search-icon" />
            <input
              type="text"
              className="pg-input att-panel-input"
              placeholder="Search available topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          <div className="att-panel-body">
            {filteredParentTopics.length === 0 &&
            filteredStandaloneTopics.length === 0 ? (
              <p className="att-panel-empty">
                {searchTerm ? "No matching topics found" : "No available topics"}
              </p>
            ) : (
              <>
                {filteredParentTopics.map((parentTopic) => {
                  const isExpanded = expandedAccordions.has(parentTopic.id);
                  const hasChildren = parentTopic.children.length > 0;

                  /* A main topic with nothing under it is a topic in its
                     own right -- the syllabus form creates headings that
                     way on purpose, and a syllabus can be nothing but
                     headings. It used to draw as a disabled accordion
                     head, so on those courses the step offered no row
                     that could be ticked and the session could not be
                     marked at all. */
                  if (!hasChildren) {
                    return (
                      <div className="att-group" key={parentTopic.id}>
                        {renderTopicOption(parentTopic)}
                      </div>
                    );
                  }

                  return (
                    <div className="att-group" key={parentTopic.id}>
                      <button
                        type="button"
                        className="att-group-head"
                        aria-expanded={isExpanded}
                        onClick={() => toggleAccordion(parentTopic.id)}
                      >
                        <span className="att-group-name">
                          {parentTopic.topic}
                        </span>
                        <span className="att-group-count">
                          {parentTopic.children.length}
                        </span>
                        <ChevronDown
                          size={14}
                          className={
                            isExpanded
                              ? "att-group-caret att-group-caret--open"
                              : "att-group-caret"
                          }
                        />
                      </button>

                      {isExpanded && (
                        <div className="att-group-body">
                          {parentTopic.children.map(renderTopicOption)}
                        </div>
                      )}
                    </div>
                  );
                })}

                {filteredStandaloneTopics.length > 0 && (
                  <div className="att-group">
                    <p className="att-group-label">Other topics</p>
                    {filteredStandaloneTopics.map(renderTopicOption)}
                  </div>
                )}
              </>
            )}
          </div>

          {!alwaysOpen && (
            <div className="att-panel-foot">
              <button type="button" className="pg-btn" onClick={closeDropdown}>
                Done
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// BatchList
//
// The batches, listed. It was an async dropdown, which meant opening a menu to
// answer the one question this step asks -- and the menu expanded over or
// pushed the rest of the step. Same shape as the student roster on step three:
// a search box, a list of rows, and a button for the next page.
//
// Search and paging both go to the server: the caller owns the fetching, this
// only says when.
// ---------------------------------------------------------------------------
const BatchList = ({
  options = [],
  value,
  onSelect,
  onSearch,
  onLoadMore,
  onOpen,
  hasMore = false,
  loading = false,
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const endRef = useRef(null);

  // The first page is fetched when the step appears, not when a menu is
  // opened -- there is no menu any more.
  useEffect(() => {
    if (onOpen) onOpen();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The next page arrives on scroll. The scrolling element is the step
  // stage, not the window, so the observer has to be told which box to
  // measure against -- found by walking up rather than hard-coded, so
  // this still works on the standalone route where the page scrolls.
  useEffect(() => {
    const node = endRef.current;
    if (!node || !hasMore) return undefined;

    let root = node.parentElement;
    while (root && root !== document.body) {
      const oy = window.getComputedStyle(root).overflowY;
      if (oy === "auto" || oy === "scroll") break;
      root = root.parentElement;
    }
    if (root === document.body) root = null;

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting) && !loading && onLoadMore) {
          onLoadMore();
        }
      },
      // Fetched before the end is reached, so the list grows below the
      // fold instead of under the eye.
      { root, rootMargin: "280px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, onLoadMore, options.length]);

  const selectedId = value ? String(value.value) : null;

  return (
    <div className="att-picklist">
      <input
        type="search"
        className="pg-input att-picklist-search"
        placeholder="Search batches by name..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (onSearch) onSearch(e.target.value);
        }}
        disabled={disabled}
        aria-label="Search batches"
      />

      {options.length === 0 ? (
        <p className="att-panel-empty">
          {loading
            ? "Loading batches..."
            : query
              ? `No batch matches “${query}”.`
              : "No batches available."}
        </p>
      ) : (
        <ul className="att-picklist-rows">
          {options.map((batch) => {
            const isOn = selectedId === String(batch.value);
            return (
              <li key={batch.value}>
                <button
                  type="button"
                  className={`att-pick${isOn ? " is-on" : ""}`}
                  aria-pressed={isOn}
                  disabled={disabled}
                  onClick={() => onSelect(batch)}
                >
                  <span className="att-pick-id">
                    <b>{batch.label}</b>
                    {(batch.days || batch.timing) && (
                      <span>
                        {[batch.days, batch.timing].filter(Boolean).join(" · ")}
                      </span>
                    )}
                  </span>
                  <span className="att-pick-mark" aria-hidden="true" />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* Always rendered while more remains, at a fixed height, so a
          batch arriving never changes the space below the list. */}
      {options.length > 0 && (
        <div className="att-picklist-foot" ref={endRef}>
          {hasMore ? (
            <>
              <span className="pg-spinner att-spinner" aria-hidden="true" />
              <span>Loading more batches...</span>
            </>
          ) : (
            <span>All {options.length} batches loaded</span>
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// StudentRoster
//
// One list, shown in place. It used to be a trigger that opened a panel of
// students, with the chosen ones repeated in Present and Absent boxes above
// it -- the same names in two places, and the marking hidden behind a click.
// Here the roster is the section: search it, and mark each row where it sits.
//
// The whole batch roster arrives in a single getBatchById call, so the search
// filters what is already loaded rather than asking the server per keystroke.
// ---------------------------------------------------------------------------
const ROSTER_PAGE = 24;

const STEP_LABELS = ["Batch", "Topics", "Students"];

// What each step is asking. Held here so the dialog can put it in its
// own title rather than the form repeating it inside the step.
const STEP_COPY = [
  {
    title: "Which batch?",
    hint: "Choose the batch this session belongs to. Its topics and students follow from it.",
  },
  {
    title: "What did you cover?",
    hint: "Tick the topics taught in this session.",
  },
  {
    title: "Who was there?",
    hint: "Mark each student present or absent.",
  },
];

const StudentRoster = ({
  options = [],
  selectedUsers = [],
  onChange,
  disabled = false,
  isCommonOff = false,
}) => {
  const [query, setQuery] = useState("");
  // How many rows are on screen. The roster is shown in full rather than
  // in a scrolling box -- the dialog is what scrolls -- so a long batch
  // grows by the button instead of all at once.
  const [shownCount, setShownCount] = useState(ROSTER_PAGE);

  useEffect(() => {
    setShownCount(ROSTER_PAGE);
  }, [query, options.length]);

  if (isCommonOff) {
    return (
      <p className="att-note att-note--warn">
        Common off — every student is on leave, so there is nothing to mark.
      </p>
    );
  }

  const idOf = (u) => String(u.studentId || u.id || u._id);

  const q = query.trim().toLowerCase();
  const shown = q
    ? options.filter((u) =>
        [u.studentName || u.name, u.studentEmail || u.email]
          .some((v) => String(v || "").toLowerCase().includes(q)),
      )
    : options;

  const statusOf = (sid) =>
    selectedUsers.find((u) => String(u.studentId || u.id || u._id) === sid)
      ?.status || null;

  const setStatus = (user, status) => {
    if (disabled) return;
    const sid = idOf(user);
    const rest = selectedUsers.filter(
      (u) => String(u.studentId || u.id || u._id) !== sid,
    );
    // Tapping the status a row already has clears it, so a student can be
    // taken back out of the record without hunting for a remove button.
    if (statusOf(sid) === status) {
      onChange(rest);
      return;
    }
    onChange([...rest, { ...user, studentId: sid, status }]);
  };

  const markAll = (status) => {
    if (disabled) return;
    onChange(
      options.map((u) => ({ ...u, studentId: idOf(u), status })),
    );
  };

  const marked = selectedUsers.length;
  const visible = shown.slice(0, shownCount);
  const remaining = shown.length - visible.length;

  return (
    <div className="att-roster-block">
      <div className="att-roster-bar">
        <input
          type="search"
          className="pg-input att-roster-search"
          placeholder="Search this batch by name or email..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={disabled || options.length === 0}
          aria-label="Search students"
        />
        <span className="att-roster-progress">
          {marked} of {options.length} marked
        </span>
        <div className="att-roster-bulk">
          <button
            type="button"
            className="pg-btn"
            onClick={() => markAll("present")}
            disabled={disabled || options.length === 0}
          >
            All present
          </button>
          <button
            type="button"
            className="pg-btn"
            onClick={() => onChange([])}
            disabled={disabled || marked === 0}
          >
            Clear
          </button>
        </div>
      </div>

      {options.length === 0 ? (
        <p className="att-panel-empty">
          Select a batch to load its students.
        </p>
      ) : shown.length === 0 ? (
        <p className="att-panel-empty">No student matches &ldquo;{query}&rdquo;.</p>
      ) : (
        <ul className="att-roster-grid">
          {visible.map((user, i) => {
            const sid = idOf(user);
            const status = statusOf(sid);
            const name = user.studentName || user.name || "Unknown";
            return (
              <li
                key={sid}
                className={`att-student${
                  status ? ` is-on is-${status}` : ""
                }`}
              >
                <span className="att-student-n">{i + 1}</span>

                <span
                  className={`pgm-avatar pgm-avatar--${avatarTone(name)}`}
                  aria-hidden="true"
                >
                  {initialsOf(name)}
                </span>

                <span className="att-student-id">
                  <b>{name}</b>
                  <span>{user.studentEmail || user.email || "No email"}</span>
                </span>

                <div className="att-mark">
                  <button
                    type="button"
                    className={`att-mark-opt att-mark-opt--good${
                      status === "present" ? " is-on" : ""
                    }`}
                    aria-pressed={status === "present"}
                    disabled={disabled}
                    onClick={() => setStatus(user, "present")}
                  >
                    Present
                  </button>
                  <button
                    type="button"
                    className={`att-mark-opt att-mark-opt--bad${
                      status === "absent" ? " is-on" : ""
                    }`}
                    aria-pressed={status === "absent"}
                    disabled={disabled}
                    onClick={() => setStatus(user, "absent")}
                  >
                    Absent
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {remaining > 0 && (
        <button
          type="button"
          className="pg-btn att-roster-more"
          onClick={() => setShownCount((n) => n + ROSTER_PAGE)}
        >
          Load {Math.min(remaining, ROSTER_PAGE)} more
          <span className="att-roster-more-rest">{remaining} left</span>
        </button>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// AddAttendance (main component)
// ---------------------------------------------------------------------------
// Rendered two ways: as the /add-attendance route, and inside the
// attendance list's own modal. The props below are what the modal passes;
// with none of them the component reads the URL and navigates on finish,
// exactly as the route always did.
function AddAttendance({
  // Shown inside a dialog rather than as a page: the component drops its
  // own header, since the dialog supplies one.
  embedded = false,
  // The record to edit. Falls back to the :id route param.
  recordId,
  // Called after a successful save instead of navigating away.
  onDone,
  // Called when the form is dismissed instead of navigating away.
  onCancel,
  // Told the current step's question, so the shell can title itself.
  onStepChange,
}) {
  const [syllabusTopics, setSyllabusTopics] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syllabusLoading, setSyllabusLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  /* Why this record cannot be saved, when it cannot: the sentence the
     API returned with it. Null means it can. The record is still loaded
     and still shown -- a trainer opening a session they can no longer
     change should see what they marked and be told why it is fixed, not
     be thrown back to the list by a toast. */
  const [editLockReason, setEditLockReason] = useState(null);
  // Which of the three questions the form is on. Editing starts at the
  // first one too, so the record can be reviewed on the way through.
  const [step, setStep] = useState(1);
  // The step currently painted, which trails `step` by one exit.
  const [shownStep, setShownStep] = useState(1);
  const [stepPhase, setStepPhase] = useState("in");

  useEffect(() => {
    if (shownStep === step) return undefined;
    setStepPhase("out");
    const t = setTimeout(() => {
      setShownStep(step);
      setStepPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [step, shownStep]);

  useEffect(() => {
    if (onStepChange) onStepChange(STEP_COPY[step - 1], step);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const [availableTopics, setAvailableTopics] = useState([]);
  const [taughtTopics, setTaughtTopics] = useState([]);
  const [originalTopics, setOriginalTopics] = useState([]);
  const [matchingSyllabuses, setMatchingSyllabuses] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [isCommonOff, setIsCommonOff] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedTrainerId, setSelectedTrainerId] = useState("");
  const [syllabusTopicCount, setSyllabusTopicCount] = useState(0);
  const [initialValues, setInitialValues] = useState({
    selectedBatch: null,
    todaysTopics: [],
    selectedDate: new Date(),
    selectedUsers: [],
    commonOff: false,
  });

  const navigate = UseNavigate();
  const { id: routeId } = UseParams();
  const id = recordId ?? routeId;

  // Leaving the form means "go back to the list" on the route, and "close
  // the dialog" when embedded. Every exit goes through these two so the
  // component never has to know which it is.
  const leave = useCallback(() => {
    if (onCancel) onCancel();
    else navigate("/list-attendance");
  }, [onCancel, navigate]);

  const finish = useCallback(() => {
    if (onDone) onDone();
    else navigate("/list-attendance");
  }, [onDone, navigate]);
  const hasLoaded = useRef(false);
  const syllabusRequestSeq = useRef(0);
  const syllabusCacheRef = useRef(new Map());

  // Dropdown pagination states for batch
  const [batchPage, setBatchPage] = useState(1);
  const [hasMoreBatches, setHasMoreBatches] = useState(true);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [batchSearchTerm, setBatchSearchTerm] = useState("");
  const [batchOptions, setBatchOptions] = useState([]);
  const [batchHasLoaded, setBatchHasLoaded] = useState(false);
  const isFetchingBatches = useRef(false);

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------
  const normalizeValue = (value) => String(value ?? "").trim();
  const safeTime = (d) => {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? "N/A" : dt.toLocaleTimeString();
  };
  const resolveTrainerRef = (raw) => {
    if (raw == null || raw === "") return "";
    if (typeof raw === "string" || typeof raw === "number") return String(raw).trim();
    if (Array.isArray(raw) && raw[0] != null) return resolveTrainerRef(raw[0]);
    if (typeof raw === "object") {
      const tid = raw.trainerId ?? raw.id ?? raw._id;
      if (tid != null && typeof tid === "object") return resolveTrainerRef(tid);
      if (tid != null) return String(tid).trim();
    }
    return "";
  };
  const toBatchOption = (batch) => ({
    label: batch.name,
    value: String(batch.id || batch._id || ""),
    courseId: resolveCourseId(batch.courseId) || "",
    userId: batch.userId,
    trainerId: resolveTrainerRef(batch.trainerId),
    days: batch.days || "",
    timing: `${safeTime(batch.startTime)} - ${safeTime(batch.endTime)}`,
    syllabusTopicCount: batch.syllabusTopicCount || 0,
  });
  const getTrainerFilterId = () => {
    if (!currentUser) return null;
    if (currentUser.isSuperAdmin === true || currentUser.isSuperAdmin === 1) return null;
    return currentUser.id || currentUser._id || null;
  };

  const mapBatchStudents = (students = []) => {
    const src = Array.isArray(students) ? students : [];
    return src
      .map((s) => {
        if (!s || typeof s !== "object") return null;
        const id = String(s.id || s._id || "").trim();
        if (!id) return null;
        return {
          studentId: id,
          userId: id,
          studentName: s.name || "Unknown",
          studentEmail: s.email || "N/A",
          studentNumber: s.phone || "N/A",
          createdAt: s.createdAt,
          createdBy: s.createdBy,
          updatedAt: s.updatedAt,
          updatedBy: s.updatedBy,
        };
      })
      .filter(Boolean);
  };

  const extractTrainerId = (batchData) => {
    if (batchData?.trainerId && Array.isArray(batchData.trainerId) && batchData.trainerId.length > 0) {
      const trainerObj = batchData.trainerId[0];
      if (trainerObj && trainerObj.id) {
        return trainerObj.id;
      }
    }
    return null;
  };

  // -------------------------------------------------------------------------
  // Topic helpers - Updated to use syllabusTopicCount
  // -------------------------------------------------------------------------
  const getTopicsFromSyllabusTopics = (courseId, topicsSource = null) => {
    const cid = resolveCourseId(courseId);
    if (!cid) return [];
    const src = Array.isArray(topicsSource) ? topicsSource : syllabusTopics;
    const courseTopics = src.filter((topic) => resolveCourseId(topic?.courseId) === cid);
    setMatchingSyllabuses(courseTopics);
    return courseTopics;
  };

  // Updated function to fetch syllabus topics with dynamic limit based on syllabusTopicCount
 const fetchSyllabusTopicsForCourse = async (courseId, topicCount = 0) => {
  const seq = (syllabusRequestSeq.current += 1);
  setSyllabusLoading(true);
  try {
    const id = resolveCourseId(courseId);
    if (!id) {
      setSyllabusTopics([]);
      return [];
    }

    // Use the syllabusTopicCount from batch response to determine limit
    const limit = topicCount > 0 ? topicCount : 49; // Default to 49 if count is 0
    
    console.log(`Fetching syllabus topics with limit: ${limit} for course: ${id}`, seq);
    
    const cached = syllabusCacheRef.current.get(id);
    if (cached?.data && cached?.limit === limit && cached?.seq === seq) {
      setSyllabusTopics(cached.data);
      return cached.data;
    }

    const resp = await listSyllabusTopics({
      courseId: id,
      limit: limit,
      page: 1,
      search: "",
      sort: "desc",
    });
    const data = Array.isArray(resp?.data) ? resp.data : [];
    
    // Only update if this is still the latest request
    if (seq === syllabusRequestSeq.current) {
      syllabusCacheRef.current.set(id, { data, limit, seq });
      setSyllabusTopics(data);
    }
    return data;
  } catch (error) {
    console.error("Error fetching syllabus topics", error);
    toast.error("Failed to load syllabus topics");
    if (seq === syllabusRequestSeq.current) {
      setSyllabusTopics([]);
    }
    return [];
  } finally {
    if (seq === syllabusRequestSeq.current) {
      setSyllabusLoading(false);
    }
  }
};

useEffect(() => {
  if (!selectedCourseId) return;
  // Only update if we have syllabus topics and they match the selected course
  if (syllabusTopics.length > 0) {
    const topics = getTopicsFromSyllabusTopics(selectedCourseId, syllabusTopics);
    setAvailableTopics(topics);
  }
}, [selectedCourseId, syllabusTopics]);

  const checkExistingAttendanceRemote = async (batchId, date, excludeId = null) => {
    const day =
      date instanceof Date
        ? date.toISOString().split("T")[0]
        : new Date(date).toISOString().split("T")[0];
    const res = await listAttendances({
      batchId,
      date: day,
      page: 1,
      limit: 10,
      search: "",
      sort: "desc",
    });
    const records = Array.isArray(res?.data) ? res.data : [];
    if (!excludeId) return records.length > 0;
    return records.some((r) => String(r?.id) !== String(excludeId));
  };

  // -------------------------------------------------------------------------
  // Fetch attendance record for edit mode
  // -------------------------------------------------------------------------
  const fetchAttendanceRecord = async (recordId, user) => {
    setLoading(true);
    try {
      const response = await getattendanceById(recordId);
      const record = response.data;

      /* Who may change this record is the API's call -- an admin any
         record, a trainer only their own and only within 48 hours of the
         session -- and it comes back on the record itself, with the
         sentence to show. This used to work the window out again here
         off profiles[0] and bounce the trainer back to the list on a
         toast, which told them nothing they could act on. */
      const editability = record.editability || null;
      setEditLockReason(editability && !editability.canEdit ? editability.reason : null);

      const batchIdForFetch =
        Array.isArray(record.batchId) && record.batchId[0]
          ? record.batchId[0].id || record.batchId[0]._id
          : record.batchId;
      const batchResp = await getBatchById(batchIdForFetch, { includeStudents: true });
      const batch = batchResp?.data;
      if (!batch) return;

      const batchOption = toBatchOption(batch);
      setSelectedBatch(batchOption);
      setSelectedCourseId(batchOption.courseId);
      const topicCount = batch.syllabusTopicCount || 0;
      setSyllabusTopicCount(topicCount);
      
      const trainerId = extractTrainerId(batch);
      setSelectedTrainerId(trainerId);

      const batchStudents = mapBatchStudents(batch.userId || []);
      const sortedBatchStudents = [...batchStudents].sort((a, b) =>
        (a.studentName || "").localeCompare(b.studentName || "")
      );
      setFilteredUsers(sortedBatchStudents);

      // Fetch syllabus topics with the topic count from batch
      await fetchSyllabusTopicsForCourse(batchOption.courseId, topicCount);

      let selectedTopics = [];
      let isCommonOffRecord = false;

      if (record.syllabusId) {
        if (record.syllabusId.toLowerCase().includes("common off")) {
          isCommonOffRecord = true;
          setIsCommonOff(true);
          selectedTopics = [];
        } else {
          selectedTopics = record.syllabusId
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean);
        }
      }

      setOriginalTopics(selectedTopics);
      setTaughtTopics([]);

      let presentIdList = [];
      if (Array.isArray(record.userIds) && record.userIds.length > 0) {
        const rosterWithFlags =
          record.userIds.length > 0 &&
          record.userIds.every(
            (u) =>
              u &&
              typeof u === "object" &&
              Object.prototype.hasOwnProperty.call(u, "isPresent") &&
              typeof u.isPresent === "boolean"
          );
        if (rosterWithFlags) {
          presentIdList = record.userIds
            .filter((u) => u.isPresent === true)
            .map((u) => String(u.id || u._id || "").trim())
            .filter(Boolean);
        } else {
          presentIdList = record.userIds
            .map((u) =>
              typeof u === "string" ? u.trim() : String(u.id || u._id || "").trim()
            )
            .filter(Boolean);
        }
      } else if (typeof record.userIds === "string" && record.userIds.trim()) {
        presentIdList = record.userIds.split(",").map((i) => i.trim()).filter(Boolean);
      }

      const selectedUsersWithStatus = isCommonOffRecord
        ? []
        : sortedBatchStudents
            .map((student) => {
              return {
                ...student,
                status: presentIdList.includes(student.studentId) ? "present" : "absent",
              };
            })
            .filter(Boolean);

      setSelectedUsers(selectedUsersWithStatus);

      setInitialValues({
        selectedBatch: batchOption,
        todaysTopics: selectedTopics,
        selectedDate: new Date(record.date),
        selectedUsers: selectedUsersWithStatus,
        commonOff: isCommonOffRecord,
      });

      setIsEditing(true);
    } catch (error) {
      console.error("Error fetching attendance record", error);
      toast.error("Failed to load attendance record");
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Effects
  // -------------------------------------------------------------------------
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const user = await fetchCurrentUser();
        if (id) {
          setIsEditing(true);
          await fetchAttendanceRecord(id, user);
        }
      } catch (error) {
        console.error("Error loading initial data", error);
      }
    };

    if (!hasLoaded.current) {
      loadInitialData();
      hasLoaded.current = true;
    }
  }, []);

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------
  const fetchCurrentUser = async () => {
    try {
      const response = await getMe();
      setCurrentUser(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching current user", error);
      toast.error("Failed to load user information");
      return null;
    }
  };

  const batchDropdownPageSize = 20;

  const fetchBatchesForDropdown = useCallback(
    async (page = 1, search = "") => {
      if (isFetchingBatches.current) return;
      // Page 1 is a fresh list, so it is never gated on hasMoreBatches:
      // that value is read from this closure, and a refresh or a new
      // search sets it back to true on the same tick this call is made,
      // which the closure cannot see.
      if (page > 1 && (loadingBatches || !hasMoreBatches)) return;

      isFetchingBatches.current = true;
      setLoadingBatches(true);
      try {
        const trainerId = getTrainerFilterId();
        const pageSize = batchDropdownPageSize;
        const resp = await getBatches(
          pageSize,
          page,
          search || "",
          "name",             // searchField
          trainerId || null,  // trainerId
          null,               // courseId
          null                // status
        );
        
        const newOptions = (Array.isArray(resp?.data) ? resp.data : []).map(toBatchOption);
        
        if (page === 1) {
          setBatchOptions(newOptions);
        } else {
          setBatchOptions((prev) => [...prev, ...newOptions]);
        }
        
        setHasMoreBatches(Boolean(resp?.pagination?.hasNext));
        setBatchPage(page);
      } catch (error) {
        console.error("Error fetching batches", error);
        toast.error("Failed to load batches");
        setBatchOptions([]);
        setHasMoreBatches(false);
      } finally {
        setLoadingBatches(false);
        isFetchingBatches.current = false;
      }
    },
    [loadingBatches, hasMoreBatches, currentUser]
  );

  const handleBatchDropdownOpen = useCallback(() => {
    if (!batchHasLoaded && !loadingBatches && batchOptions.length === 0) {
      setBatchHasLoaded(true);
      fetchBatchesForDropdown(1, "");
    }
  }, [batchHasLoaded, loadingBatches, fetchBatchesForDropdown, batchOptions.length]);

  const batchSearchTimer = useRef(null);
  const handleBatchSearch = useCallback(
    (searchTerm) => {
      setBatchSearchTerm(searchTerm);
      setBatchHasLoaded(true);
      setBatchPage(1);
      setHasMoreBatches(true);
      if (batchSearchTimer.current) clearTimeout(batchSearchTimer.current);
      batchSearchTimer.current = setTimeout(() => {
        fetchBatchesForDropdown(1, searchTerm);
      }, 350);
    },
    [fetchBatchesForDropdown]
  );

  useEffect(
    () => () => {
      if (batchSearchTimer.current) clearTimeout(batchSearchTimer.current);
    },
    [],
  );

  const handleLoadMoreBatches = useCallback(() => {
    if (hasMoreBatches && !loadingBatches && !isFetchingBatches.current) {
      fetchBatchesForDropdown(batchPage + 1, batchSearchTerm);
    }
  }, [hasMoreBatches, loadingBatches, fetchBatchesForDropdown, batchPage, batchSearchTerm]);

  const handleRefreshBatches = useCallback(async () => {
    setBatchSearchTerm("");
    setBatchPage(1);
    setHasMoreBatches(true);
    setBatchHasLoaded(false);
    setBatchOptions([]);
    await fetchBatchesForDropdown(1, "");
  }, [fetchBatchesForDropdown]);

  // -------------------------------------------------------------------------
  // Batch selection handler - Updated to use syllabusTopicCount correctly
  // -------------------------------------------------------------------------
  const handleBatchSelection = async (selectedBatchOption, setFieldValue) => {
  // Reset all states first
  setFieldValue("selectedBatch", selectedBatchOption);
  setFieldValue("todaysTopics", []);
  setFieldValue("selectedUsers", []);
  setFieldValue("commonOff", false);
  setSelectedUsers([]);
  setIsCommonOff(false);
  setFilteredUsers([]);
  setAvailableTopics([]);
  setTaughtTopics([]);
  setMatchingSyllabuses([]);
  setSelectedCourseId("");
  setSelectedBatch(selectedBatchOption);
  setSelectedTrainerId("");
  setSyllabusTopicCount(selectedBatchOption?.syllabusTopicCount || 0);

  if (!selectedBatchOption) return;

  setUsersLoading(true);
  let courseIdForTopics = resolveCourseId(selectedBatchOption.courseId);
  
  try {
    const batchResp = await getBatchById(selectedBatchOption.value, { includeStudents: true });
    const batch = batchResp?.data;
    
    if (!batch) {
      toast.error("Failed to fetch batch details");
      setUsersLoading(false);
      return;
    }
    
    const courseIdResolved =
      resolveCourseId(batch?.courseId) || resolveCourseId(selectedBatchOption.courseId);
    
    const trainerId = extractTrainerId(batch);
    if (!trainerId) {
      toast.warning("Trainer not found for this batch. Please ensure a trainer is assigned.");
    }
    setSelectedTrainerId(trainerId || "");
    
    // Get the topic count from the batch response
    const topicCount = batch.syllabusTopicCount || 0;
    setSyllabusTopicCount(topicCount);
    
    const trainerResolved = trainerId || resolveTrainerRef(selectedBatchOption.trainerId);

    const mergedOption = {
      ...selectedBatchOption,
      label: batch?.name || selectedBatchOption.label,
      value: String(batch?.id || batch?._id || selectedBatchOption.value || ""),
      courseId: courseIdResolved,
      trainerId: trainerResolved || "",
      userId: batch?.userId ?? selectedBatchOption.userId,
      days: batch?.days ?? selectedBatchOption.days,
      timing: batch
        ? `${safeTime(batch.startTime)} - ${safeTime(batch.endTime)}`
        : selectedBatchOption.timing,
      syllabusTopicCount: topicCount,
    };
    setFieldValue("selectedBatch", mergedOption);
    setSelectedBatch(mergedOption);
    setSelectedCourseId(courseIdResolved);
    courseIdForTopics = courseIdResolved;

    const batchStudents = mapBatchStudents(batch.userId || []);
    const sorted = [...batchStudents].sort((a, b) =>
      (a.studentName || "").localeCompare(b.studentName || "")
    );
    setFilteredUsers(sorted);
    
    if (!isEditing) {
      const allPresent = sorted.map((u) => ({ ...u, status: "present" }));
      setSelectedUsers(allPresent);
      setFieldValue("selectedUsers", allPresent);
    }

    // Fetch syllabus topics - wait for it to complete
    const topics = await fetchSyllabusTopicsForCourse(courseIdForTopics, topicCount);
    setTaughtTopics([]);
    
    // Update available topics after fetch completes
    const courseTopics = topics.filter((topic) => resolveCourseId(topic?.courseId) === courseIdResolved);
    setAvailableTopics(courseTopics);
    setMatchingSyllabuses(courseTopics);
    
  } catch (error) {
    console.error("Error fetching batch details:", error);
    toast.error("Failed to load batch details");
  } finally {
    setUsersLoading(false);
  }
};


  // -------------------------------------------------------------------------
  // Common Off toggle
  // -------------------------------------------------------------------------
  const handleCommonOffChange = (checked, setFieldValue) => {
    setIsCommonOff(checked);
    setFieldValue("commonOff", checked);

    if (checked) {
      setFieldValue("todaysTopics", []);
      setFieldValue("selectedUsers", []);
      setSelectedUsers([]);
    } else {
      if (filteredUsers.length > 0) {
        const allPresent = filteredUsers.map((u) => ({ ...u, status: "present" }));
        setSelectedUsers(allPresent);
        setFieldValue("selectedUsers", allPresent);
      }
    }
  };

  // -------------------------------------------------------------------------
  // Submit
  // -------------------------------------------------------------------------
  const handleSubmitAttendance = async (values, { resetForm }) => {
    // Locked records: the note above the form already says why, and the
    // server would refuse this anyway. Say it once more where the press
    // happened, and go no further.
    if (editLockReason) {
      toast.error(editLockReason);
      return;
    }

    if (!values.selectedBatch) {
      toast.error("Please select a batch");
      return;
    }

    if (!selectedTrainerId && !values.commonOff) {
      toast.error("Trainer not found for this batch. Cannot mark attendance without a trainer.");
      return;
    }

    if (!values.commonOff && !values.selectedUsers?.length) {
      toast.error("Please select students or check common off");
      return;
    }

    if (!isEditing) {
      const isDuplicate = await checkExistingAttendanceRemote(
        values.selectedBatch.value,
        values.selectedDate
      );
      if (isDuplicate) {
        toast.error("Attendance has already been marked for this batch on this date.", {
          position: "top-right",
          autoClose: 5000,
        });
        return;
      }
    }

    try {
      const selectedDate =
        values.selectedDate instanceof Date ? values.selectedDate : new Date(values.selectedDate);
      const selectedDay = selectedDate
        .toLocaleDateString("en-US", { weekday: "long" })
        .toLowerCase();

      let daysString = values.selectedBatch?.days || "";
      const allowedDays = daysString
        .split(",")
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean);

      const isAdmin = currentUser?.isSuperAdmin;
      if (!isAdmin && allowedDays.length > 0 && !allowedDays.includes(selectedDay)) {
        toast.error(
          `Attendance can only be marked on scheduled batch days (${allowedDays.join(
            ", "
          )}). Selected day: ${selectedDay}`
        );
        return;
      }
    } catch (e) {
      console.error("Day validation error:", e);
      if (!currentUser?.isSuperAdmin) {
        toast.error("Unable to validate batch day. Please try again later.");
        return;
      }
    }

    let attendanceData;

    if (values.commonOff) {
      attendanceData = {
        batchId: values.selectedBatch.value,
        userIds: "No students present",
        syllabusId: "common off taken by the all students",
        trainerId: selectedTrainerId || resolveTrainerRef(values.selectedBatch.trainerId) || "",
        date: values.selectedDate.toISOString(),
        totalLectures: "N/A",
        attendance: "N/A",
      };
    } else {
      const presentUsers = values.selectedUsers.filter((u) => u.status === "present");
      const presentUserIds = presentUsers.map((u) => u.studentId).join(",");

      if (presentUsers.length === 0) {
        toast.error("At least one user must be marked as present or check common off");
        return;
      }

      if (values.todaysTopics.length === 0) {
        toast.error("Please select today's topics before marking attendance.");
        return;
      }

      let syllabusIdToSend;
      if (values.todaysTopics.length === 0) {
        syllabusIdToSend = "";
      } else {
        const newTopicsOnly = values.todaysTopics.filter((t) => !taughtTopics.includes(t));
        syllabusIdToSend = newTopicsOnly.join(", ");
      }

      attendanceData = {
        batchId: values.selectedBatch.value,
        userIds: presentUserIds,
        syllabusId: syllabusIdToSend,
        trainerId: selectedTrainerId || resolveTrainerRef(values.selectedBatch.trainerId) || "",
        date: values.selectedDate.toISOString(),
        totalLectures: "N/A",
        attendance: "N/A",
      };
    }

    if (!attendanceData.trainerId) {
      toast.error("Trainer ID is missing. Cannot submit attendance.");
      return;
    }

    setLoading(true);
    try {
      let response;
      if (isEditing) {
        response = await updateattendance(id, attendanceData);
      } else {
        response = await createattendance(attendanceData);
      }

      setTimeout(() => {
        /* The API's `message` is NOT a string. utils/constant.js
           getMessage() returns { code, message, ariaLabel }, so handing
           response.message straight to the toast printed the object --
           "[object Object]" where the confirmation should be. Take the
           string out of it, and still accept a plain string in case an
           endpoint returns one. */
        const raw = response?.message;
        const successMessage =
          (typeof raw === "string" ? raw : raw?.message) ||
          (isEditing ? "Attendance updated successfully!" : "Attendance marked successfully!");

        toast.success(successMessage, {
          position: "top-right",
          autoClose: 2500,
        });

        resetForm();
        setSelectedUsers([]);
        setFilteredUsers([]);
        setIsCommonOff(false);

        // On the route: long enough for the success toast to be read.
        // Embedded: the dialog shows the message in place of this form and
        // closes itself, so the list behind it reloads at once.
        if (embedded) finish();
        else setTimeout(finish, 2700);
      }, 100);
    } catch (error) {
      /* Same shape trap as the success path: the API's `message` is
         { code, message, ariaLabel }, so a refusal handed straight to the
         toast printed "[object Object]" -- exactly where the reason
         matters most. Take the string out of whichever field carried it. */
      const textOf = (value) =>
        typeof value === "string" ? value : value && typeof value === "object" ? value.message : null;
      const body = error.response?.data;
      const errorMessage =
        textOf(body?.error?.message) ||
        textOf(body?.error) ||
        textOf(body?.message) ||
        error.message ||
        "Failed to submit attendance";

      // 403 here is the edit rule: not their record, or the 48 hours are
      // up. Keep the reason on the form, not just in a toast that goes.
      if (error.response?.status === 403) setEditLockReason(errorMessage);

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 8000,
      });
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------------
  // Validation schema
  // -------------------------------------------------------------------------
  const validationSchema = Yup.object().shape({
    selectedBatch: Yup.object().required("Batch selection is required"),
    todaysTopics: Yup.array(),
    selectedDate: Yup.date().required("Date is required"),
    selectedUsers: Yup.array().when("commonOff", {
      is: false,
      then: (schema) => schema.min(1, "At least one user must be selected or check common off"),
      otherwise: (schema) => schema,
    }),
    commonOff: Yup.boolean(),
  });

  // -------------------------------------------------------------------------
  // Permission guard
  // -------------------------------------------------------------------------
  const hasShownToast = useRef(false);
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (!hasPermission("add-attendance", "view", "lms")) {
      if (!hasShownToast.current) {
        toast.error("You do not have permission to add attendance.");
        navigate("/");
        hasShownToast.current = true;
      }
    }
  }, [hasPermission, navigate]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <div className={embedded ? "att-embed" : "pg"}>
      {/* The dialog draws its own title, so the page header is only for
          the standalone route. */}
      {!embedded && (
        <header className="pg-head">
          <div className="pg-head-main">
            <h4 className="pg-title">
              {isEditing ? "Edit attendance" : "Mark attendance"}
            </h4>
            <p className="pg-sub">
              {isEditing
                ? "Update an existing attendance record."
                : "Record who attended and what the session covered."}
            </p>
          </div>
          <div className="pg-head-actions">
            <button type="button" className="pg-btn" onClick={leave}>
              All attendance
            </button>
          </div>
        </header>
      )}

      {loading && !isEditing ? (
        <div className="pg-state">
          <span className="pg-spinner" aria-hidden="true" />
          <p className="pg-state-text">Loading attendance data...</p>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmitAttendance}
          enableReinitialize
        >
          {({ values, setFieldValue, handleSubmit, errors, touched }) => (
            <form onSubmit={handleSubmit} className="att-form" noValidate>
              {/* Read before the record is: what was marked is still all
                  here, prefilled, it simply cannot be changed from here. */}
              {editLockReason && (
                <p className="att-note att-note--warn" role="status">
                  {editLockReason}
                </p>
              )}

              {/* One question at a time: which batch, then what was
                  covered, then who was there. Each answer is what makes
                  the next question answerable, so they are steps rather
                  than three panels competing for attention. */}
              {/* The batch, its timing and the session date, stated once
                  above the steps. They were disabled fields inside step
                  one, which read as things you could edit and were what
                  the expanding batch list pushed down the page. */}
              {values.selectedBatch && (
                <dl className="att-context">
                  <div>
                    <dt>Batch</dt>
                    <dd>{values.selectedBatch.label || values.selectedBatch.name}</dd>
                  </div>
                  <div>
                    <dt>Date</dt>
                    <dd>
                      {values.selectedDate.toLocaleDateString("en-GB", {
                        weekday: "short",
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </dd>
                  </div>
                  {values.selectedBatch.timing && (
                    <div>
                      <dt>Timing</dt>
                      <dd>{values.selectedBatch.timing}</dd>
                    </div>
                  )}
                </dl>
              )}

              <ol className="att-steps">
                {STEP_LABELS.map((label, i) => {
                  const n = i + 1;
                  const state =
                    n === step ? "is-now" : n < step ? "is-done" : "";
                  return (
                    <li className={`att-step-tab ${state}`.trim()} key={label}>
                      <button
                        type="button"
                        // Adding: only backwards, a step ahead has not been
                        // answered yet. Editing: every step is filled, so any tab opens.
                        disabled={n === step || (!isEditing && n > step)}
                        onClick={() => setStep(n)}
                      >
                        <span className="att-step-n">{n}</span>
                        {label}
                      </button>
                    </li>
                  );
                })}
              </ol>

              {/* Painted from shownStep, not step: the step on screen
                  stays until its exit finishes. Keying on step alone
                  swapped the content in a single frame -- the old step
                  vanished with no exit while the new one animated in
                  from nothing, which is what read as a jump. */}
              <div className={`att-stage is-${stepPhase}`}>
                <div className="att-stage-inner" key={shownStep}>
                {shownStep === 1 && (
                  <section className="att-step">
                    {!embedded && (
                      <header className="att-step-head">
                        <h5>{STEP_COPY[0].title}</h5>
                        <p>{STEP_COPY[0].hint}</p>
                      </header>
                    )}

                    <div className="att-onefield">
                      <BatchList
                        options={batchOptions}
                        value={selectedBatch}
                        loading={loadingBatches}
                        hasMore={hasMoreBatches}
                        disabled={isEditing}
                        onOpen={handleBatchDropdownOpen}
                        onSearch={handleBatchSearch}
                        onLoadMore={handleLoadMoreBatches}
                        // Selecting a batch selects a batch. The form
                        // moves on when Continue is clicked, and not
                        // before -- nothing here advances on its own.
                        onSelect={(batch) =>
                          handleBatchSelection(batch, setFieldValue)
                        }
                      />
                      {errors.selectedBatch && touched.selectedBatch && (
                        <p className="att-error">{errors.selectedBatch}</p>
                      )}
                    </div>

                    {selectedBatch && !selectedTrainerId && !values.commonOff && (
                      <p className="att-note att-note--warn">
                        No trainer is assigned to this batch. Assign one before
                        marking attendance.
                      </p>
                    )}
                  </section>
                )}

                {shownStep === 2 && (
                  <section className="att-step">
                    <header className="att-step-head att-step-head--slim">
                      <h5>
                        {!embedded && STEP_COPY[1].title}
                        {availableTopics.length > 0 && !values.commonOff && (
                          <span className="att-card-count">
                            {values.todaysTopics.length} of{" "}
                            {availableTopics.length} chosen
                          </span>
                        )}
                        {syllabusLoading && (
                          <span
                            className="pg-spinner att-spinner"
                            aria-hidden="true"
                          />
                        )}
                      </h5>
                      {!embedded && <p>{STEP_COPY[1].hint}</p>}
                    </header>

                    {!values.commonOff && (
                      <>

                        <MultiSelectTopicsDropdown
                          options={availableTopics}
                          selectedValues={values.todaysTopics}
                          onChange={(selectedTopics) =>
                            setFieldValue("todaysTopics", selectedTopics)
                          }
                          disabled={!values.selectedBatch || syllabusLoading}
                          taughtTopics={taughtTopics}
                          isCommonOff={values.commonOff}
                          alwaysOpen
                        />

                        {errors.todaysTopics && touched.todaysTopics && (
                          <p className="att-error">{errors.todaysTopics}</p>
                        )}

                        {values.selectedBatch &&
                          availableTopics.length === 0 &&
                          !syllabusLoading && (
                            <p className="att-note">
                              No topics available. Check that syllabus topics
                              are configured for this course.
                            </p>
                          )}
                      </>
                    )}
                  </section>
                )}

                {shownStep === 3 && (
                  <section className="att-step">
                    {!embedded && (
                      <header className="att-step-head">
                        <h5>{STEP_COPY[2].title}</h5>
                        <p>{STEP_COPY[2].hint}</p>
                      </header>
                    )}

                    {/* Small and out of the way: it is the exception, and
                        it belongs with the students it overrides rather
                        than on the topics step. */}
                    <label
                      className={`att-switch${
                        values.commonOff ? " is-on" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        id="commonOff"
                        checked={values.commonOff}
                        onChange={(e) =>
                          handleCommonOffChange(e.target.checked, setFieldValue)
                        }
                      />
                      <span className="att-switch-track" aria-hidden="true">
                        <span className="att-switch-knob" />
                      </span>
                      <span className="att-switch-label">
                        Common off
                        <span>nobody attended today</span>
                      </span>
                    </label>

                    {values.commonOff ? (
                      <p className="att-note att-note--warn">
                        Common off — every student is on leave, so there is
                        nothing to mark. Save to record the session.
                      </p>
                    ) : (
                      <>
                        <StudentRoster
                          options={filteredUsers}
                          selectedUsers={values.selectedUsers}
                          onChange={(selectedUsers) => {
                            setFieldValue("selectedUsers", selectedUsers);
                            setSelectedUsers(selectedUsers);
                          }}
                          disabled={usersLoading}
                          isCommonOff={values.commonOff}
                        />

                        {errors.selectedUsers && touched.selectedUsers && (
                          <p className="att-error">{errors.selectedUsers}</p>
                        )}
                      </>
                    )}
                  </section>
                )}
                </div>
              </div>

              <div className="att-actions">
                {!values.commonOff &&
                  step === 3 &&
                  values.selectedUsers &&
                  values.selectedUsers.length > 0 && (
                    <span className="att-badge">
                      <span className="att-badge-part att-badge-part--good">
                        {
                          values.selectedUsers.filter(
                            (u) => u.status === "present",
                          ).length
                        }{" "}
                        present
                      </span>
                      <span className="att-badge-part att-badge-part--bad">
                        {
                          values.selectedUsers.filter(
                            (u) => u.status === "absent",
                          ).length
                        }{" "}
                        absent
                      </span>
                    </span>
                  )}

                {values.commonOff && step === 3 && (
                  <span className="att-badge">
                    <span className="att-badge-part att-badge-part--warn">
                      Common off
                    </span>
                  </span>
                )}

                {step > 1 ? (
                  <button
                    type="button"
                    className="pg-btn"
                    onClick={() => setStep(step - 1)}
                  >
                    Back
                  </button>
                ) : (
                  (isEditing || embedded) && (
                    <button type="button" className="pg-btn" onClick={leave}>
                      Cancel
                    </button>
                  )
                )}

                {step === 1 && (
                  <button
                    type="button"
                    className="pg-btn pg-btn--main"
                    disabled={!values.selectedBatch}
                    onClick={() => setStep(2)}
                  >
                    Continue
                  </button>
                )}

                {step === 2 && (
                  <button
                    type="button"
                    className="pg-btn pg-btn--main"
                    onClick={() => setStep(3)}
                  >
                    Continue
                  </button>
                )}

                {step === 3 && hasPermission("add-attendance", "add", "lms") && (
                  <button
                    type="submit"
                    className="pg-btn pg-btn--main"
                    title={editLockReason || undefined}
                    disabled={
                      loading ||
                      Boolean(editLockReason) ||
                      !values.selectedBatch ||
                      (!values.commonOff &&
                        (!selectedTrainerId ||
                          !values.selectedUsers ||
                          values.selectedUsers.length === 0))
                    }
                  >
                    {loading ? (
                      <>
                        <span
                          className="pg-spinner att-spinner"
                          aria-hidden="true"
                        />
                        {isEditing ? "Updating..." : "Marking..."}
                      </>
                    ) : isEditing ? (
                      "Update attendance"
                    ) : (
                      "Mark attendance"
                    )}
                  </button>
                )}
              </div>
            </form>
          )}
        </Formik>
      )}

    </div>
  );
}

export default AddAttendance;